import { Component, useMemo, useState, useEffect, useCallback } from "react";
import "./App.css";

// Prevents one malformed field from blanking the whole app.
class ErrorBoundary extends Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  handleReset = () => {
    try {
      localStorage.removeItem("mealPlannerResult");
      localStorage.removeItem("mealPlannerPlanId");
    } catch {
      // ignore
    }
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="empty-state">
          <div className="empty-icon">⚠️</div>
          <h3>Something rendered incorrectly</h3>
          <p>
            This plan couldn't be displayed. Clearing it and reloading usually
            fixes it.
          </p>
          <button className="primary-button" type="button" onClick={this.handleReset}>
            Clear plan & reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const API_URL = process.env.REACT_APP_API_URL;
const API_KEY = process.env.REACT_APP_API_KEY;
const FEEDBACK_EMAIL =
  process.env.REACT_APP_FEEDBACK_EMAIL || "colesmith4+pantry@hotmail.com";

const LOADING_TIPS = [
  "Checking your pantry items...",
  "Balancing your budget...",
  "Finding complementary flavors...",
  "Planning for leftovers...",
  "Assembling your grocery list...",
];

const SLOT_ORDER = ["Breakfast", "Lunch", "Dinner"];

const initialForm = {
  householdSize: 2,
  budget: 120,
  dietPreference: "Balanced",
  avoidFoods: "",
  pantryItems: "",
  days: 5,
  mealSlots: ["Dinner"],
};

function useLocalStorage(key, fallback) {
  const [value, setValue] = useState(() => {
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : fallback;
    } catch {
      return fallback;
    }
  });

  const set = useCallback(
    (next) => {
      setValue(next);
      if (next === null || next === undefined) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, JSON.stringify(next));
      }
    },
    [key],
  );

  return [value, set];
}

function Toast({ message, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 2200);
    return () => clearTimeout(t);
  }, [onDone]);

  return <div className="toast">{message}</div>;
}

function MealCard({ meal, index }) {
  const [open, setOpen] = useState(false);

  return (
    <article className="meal-card">
      <button
        className="meal-card-toggle"
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <div className="meal-card-top">
          <div className="meal-card-title-block">
            <span className="meal-index">Meal {index + 1}</span>
            <h4>{meal.name}</h4>
          </div>
          <div className="meal-card-right">
            <div className="price-pill">${meal.estimatedCost}</div>
            <span className="chevron" aria-hidden="true">
              {open ? "▲" : "▼"}
            </span>
          </div>
        </div>
        <p className="meal-description">{meal.shortDescription}</p>
        <div className="meal-meta-pills">
          {meal.mealType && (
            <span className={`meta-pill mealtype-${meal.mealType?.toLowerCase()}`}>
              {meal.mealType === "Breakfast" ? "🌅" : meal.mealType === "Lunch" ? "☀️" : "🌙"} {meal.mealType}
            </span>
          )}
          {meal.difficulty && (
            <span className={`meta-pill difficulty-${meal.difficulty?.toLowerCase()}`}>
              {meal.difficulty}
            </span>
          )}
          {meal.prepTime && (
            <span className="meta-pill">⏱ {meal.prepTime} min prep</span>
          )}
          {meal.cookTime && (
            <span className="meta-pill">🔥 {meal.cookTime} min cook</span>
          )}
        </div>
      </button>

      {open && (
        <div className="meal-card-body">
          <div className="meal-meta">
            <div className="meal-meta-block">
              <span className="meta-title">Why it fits</span>
              <p>{meal.whyThisFits}</p>
            </div>
            <div className="meal-meta-block">
              <span className="meta-title">Leftovers</span>
              <p>{meal.leftovers}</p>
            </div>
          </div>

          <div className="meal-columns">
            <div>
              <span className="meta-title">Ingredients</span>
              <ul className="clean-list">
                {meal.ingredientsNeeded?.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </div>

            <div>
              <span className="meta-title">Instructions</span>
              <ol className="clean-list ordered">
                {meal.instructions?.map((step, idx) => (
                  <li key={idx}>{step}</li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      )}
    </article>
  );
}

function isPantryMatch(groceryItem, pantryItems) {
  if (!pantryItems.length) return false;
  const haystack = groceryItem.toLowerCase();
  return pantryItems.some((p) => {
    const needle = p.toLowerCase().trim();
    if (!needle) return false;
    // match if the pantry word appears as a whole word inside the grocery item
    return new RegExp(`\\b${needle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`).test(haystack);
  });
}

function GroceryList({ items, pantryItems = [], pantryUsed = [], onCopy, onShare }) {
  const [checked, setChecked] = useState({});

  const safeItems = Array.isArray(items) ? items : [];
  const safePantryUsed = Array.isArray(pantryUsed) ? pantryUsed : [];

  const shoppableIndices = safeItems.reduce((acc, item, idx) => {
    if (typeof item === "string" && !item.startsWith("---")) acc.push(idx);
    return acc;
  }, []);

  const toggle = (idx) =>
    setChecked((prev) => ({ ...prev, [idx]: !prev[idx] }));

  return (
    <section className="subpanel">
      <div className="section-header">
        <h3>Grocery list</h3>
        <span className="grocery-count">
          {shoppableIndices.filter((i) => checked[i]).length}/{shoppableIndices.length}
        </span>
      </div>
      <ul className="grocery-checklist">
        {safeItems.map((item, idx) => {
          const isHeader = typeof item === "string" && item.startsWith("---");
          if (isHeader) {
            return (
              <li key={idx} className="grocery-category">
                {item.replace(/---/g, "").trim()}
              </li>
            );
          }
          const inPantry = isPantryMatch(item, pantryItems);
          return (
            <li
              key={idx}
              className={`grocery-item ${checked[idx] ? "checked" : ""} ${inPantry ? "in-pantry" : ""}`}
              onClick={() => toggle(idx)}
            >
              <span className="grocery-check" aria-hidden="true">
                {checked[idx] ? "✓" : ""}
              </span>
              <span className="grocery-item-text">{item}</span>
              {inPantry && (
                <span className="pantry-flag" title="You listed this as a pantry item">
                  In pantry
                </span>
              )}
            </li>
          );
        })}

        {safePantryUsed.length > 0 && (
          <>
            <li className="grocery-category">From your pantry — already have</li>
            {safePantryUsed.map((item, idx) => (
              <li
                key={`pantry-${idx}`}
                className="grocery-item checked in-pantry pantry-owned"
                title="You already have this — no need to buy"
              >
                <span className="grocery-check" aria-hidden="true">
                  ✓
                </span>
                <span className="grocery-item-text">{item}</span>
                <span className="pantry-flag">In pantry</span>
              </li>
            ))}
          </>
        )}
      </ul>
      <div className="action-row">
        <button className="copy-button" type="button" onClick={onCopy}>
          Copy list
        </button>
        <button className="copy-button" type="button" onClick={onShare}>
          Share
        </button>
      </div>
    </section>
  );
}

function PantryUsage({ report, pantryItems = [] }) {
  // Prefer the backend report; fall back to nothing if absent.
  if (!report && !pantryItems.length) return null;

  const used = report?.used ?? [];
  const unused = report?.unused ?? [];
  const total = report?.totalProvided ?? pantryItems.length;
  const usedCount = report?.totalUsed ?? used.length;

  if (!total) return null;

  const pct = total ? Math.round((usedCount / total) * 100) : 0;

  return (
    <section className="subpanel">
      <div className="section-header">
        <h3>Pantry usage</h3>
        <span className="grocery-count">
          {usedCount}/{total} used
        </span>
      </div>

      <div className="pantry-usage-bar" aria-hidden="true">
        <div className="pantry-usage-fill" style={{ width: `${pct}%` }} />
      </div>

      {used.length > 0 && (
        <div className="pantry-usage-block">
          <span className="meta-title">Used in your plan</span>
          <div className="chips">
            {used.map((item, idx) => (
              <span className="chip chip-success" key={`u-${idx}`}>
                ✓ {item}
              </span>
            ))}
          </div>
        </div>
      )}

      {unused.length > 0 && (
        <div className="pantry-usage-block">
          <span className="meta-title">Not used — and why</span>
          <ul className="pantry-unused-list">
            {unused.map((entry, idx) => (
              <li key={`n-${idx}`}>
                <span className="pantry-unused-item">{entry.item}</span>
                <span className="pantry-unused-reason">{entry.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function Modal({ title, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-card glass"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>{title}</h2>
          <button
            className="modal-close"
            type="button"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

function HowToUse() {
  const steps = [
    ["Set the basics", "Enter your household size and weekly budget so meals are portioned and priced for you."],
    ["Pick days & meals", "Choose how many days to plan and toggle Breakfast / Lunch / Dinner. The total meal count and per-meal budget update live."],
    ["Choose a diet", "Pick a diet preference — from Balanced to Keto, Vegan, or Carnivore. Each tailors the whole plan."],
    ["List pantry items", "Add ingredients you already have. The planner builds meals around them and flags them as owned in your grocery list."],
    ["Avoid foods", "List anything to keep out — allergens, dislikes — and they'll never appear."],
    ["Generate & shop", "Hit Generate. Tap any meal to expand the recipe, then copy or share your categorized grocery list."],
  ];
  return (
    <div className="howto">
      <ol className="howto-list">
        {steps.map(([title, body], i) => (
          <li key={i}>
            <span className="howto-num">{i + 1}</span>
            <div>
              <strong>{title}</strong>
              <p>{body}</p>
            </div>
          </li>
        ))}
      </ol>
      <div className="howto-tip">
        💡 Every pantry item you list is guaranteed to be used. Check the
        Pantry usage panel to see where each one landed.
      </div>
    </div>
  );
}

function FeatureRequest({ onClose, onSent }) {
  const [type, setType] = useState("Feature idea");
  const [message, setMessage] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    const subject = encodeURIComponent(`PantryPlanner — ${type}`);
    const body = encodeURIComponent(
      `${message.trim()}\n\n— Sent from PantryPlanner`,
    );
    window.location.href = `mailto:${FEEDBACK_EMAIL}?subject=${subject}&body=${body}`;
    onSent();
    onClose();
  };

  return (
    <form className="feature-form" onSubmit={submit}>
      <p className="feature-intro">
        Got an idea, or hit a bug? Send it over — it opens your email with the
        details prefilled.
      </p>

      <div className="field">
        <label htmlFor="frType">Type</label>
        <select id="frType" value={type} onChange={(e) => setType(e.target.value)}>
          <option>Feature idea</option>
          <option>Bug report</option>
          <option>General feedback</option>
        </select>
      </div>

      <div className="field">
        <label htmlFor="frMessage">Your message</label>
        <textarea
          id="frMessage"
          rows="5"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="I'd love it if PantryPlanner could…"
          autoFocus
        />
      </div>

      <button className="primary-button submit-button" type="submit" disabled={!message.trim()}>
        Send via email
      </button>
    </form>
  );
}

function TopBar({ onHowTo, onFeature }) {
  return (
    <header className="topbar">
      <div className="topbar-brand">
        <span className="brand-logo">🍽️</span>
        <span className="brand-name">PantryPlanner</span>
      </div>
      <nav className="topbar-actions">
        <button className="topbar-button" type="button" onClick={onHowTo}>
          <span aria-hidden="true">📖</span> How to use
        </button>
        <button className="topbar-button" type="button" onClick={onFeature}>
          <span aria-hidden="true">💡</span> Feature request
        </button>
      </nav>
    </header>
  );
}

function App() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [loadingTip, setLoadingTip] = useState(0);
  const [result, setResult] = useLocalStorage("mealPlannerResult", null);
  const [planId, setPlanId] = useLocalStorage("mealPlannerPlanId", "");
  const [planHistory, setPlanHistory] = useLocalStorage(
    "mealPlannerHistory",
    [],
  );
  const [activePantryItems, setActivePantryItems] = useLocalStorage("mealPlannerActivePantry", []);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("current");
  const [toast, setToast] = useState(null);
  const [modal, setModal] = useState(null); // "howto" | "feature" | null

  useEffect(() => {
    if (!loading) return;
    const interval = setInterval(
      () => setLoadingTip((t) => (t + 1) % LOADING_TIPS.length),
      1800,
    );
    return () => clearInterval(interval);
  }, [loading]);

  const showToast = (msg) => setToast(msg);

  const parsedAvoidFoods = useMemo(
    () =>
      form.avoidFoods
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    [form.avoidFoods],
  );

  const parsedPantryItems = useMemo(
    () =>
      form.pantryItems
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    [form.pantryItems],
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const buildGroceryText = (checkbox) => {
    if (!result?.groceryList) return "";
    const line = (item) =>
      item.startsWith("---")
        ? `\n${item.replace(/---/g, "").trim().toUpperCase()}`
        : `${checkbox ? "- [ ] " : ""}${item}`;
    let text = result.groceryList.map(line).join("\n");

    const pantryUsed = result.pantryReport?.used ?? [];
    if (pantryUsed.length > 0) {
      text += `\n\nFROM YOUR PANTRY (already have)\n`;
      text += pantryUsed.map((item) => `${checkbox ? "- [x] " : "✓ "}${item}`).join("\n");
    }
    return text.trim();
  };

  const handleCopyGroceryList = async () => {
    if (!result?.groceryList) return;
    const text = buildGroceryText(false);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
    showToast("Grocery list copied!");
  };

  const handleShare = async () => {
    if (!result?.groceryList) return;
    const text = buildGroceryText(true);
    if (navigator.share) {
      await navigator.share({ title: "Grocery List", text });
    } else {
      showToast("Sharing not supported on this device");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setResult(null);
    setPlanId("");
    setLoadingTip(0);

    try {
      const payload = {
        householdSize: Number(form.householdSize),
        budget: Number(form.budget),
        dietPreference: form.dietPreference,
        avoidFoods: parsedAvoidFoods,
        pantryItems: parsedPantryItems,
        days: Number(form.days),
        mealSlots: form.mealSlots,
      };

      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(API_KEY && { "x-api-key": API_KEY }),
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || data?.details || "Something went wrong.");
      }

      const newPlanId = data.planId || `plan-${Date.now()}`;
      setResult(data.mealPlan);
      setPlanId(newPlanId);
      setActivePantryItems(parsedPantryItems);

      const newEntry = {
        id: newPlanId,
        mealPlan: data.mealPlan,
        createdAt: new Date().toISOString(),
        formData: { ...form },
      };
      setPlanHistory([newEntry, ...planHistory.slice(0, 9)]);
      setActiveTab("current");
    } catch (err) {
      setError(err.message || "Unable to generate your meal plan.");
    } finally {
      setLoading(false);
    }
  };

  const handleClearCurrent = () => {
    setResult(null);
    setError("");
    setPlanId("");
    setActivePantryItems([]);
    setActiveTab("current");
  };

  const handleClearAll = () => {
    setForm(initialForm);
    setResult(null);
    setError("");
    setPlanId("");
    setActivePantryItems([]);
    setPlanHistory([]);
    setActiveTab("current");
  };

  const handleViewPlan = (plan) => {
    setResult(plan.mealPlan);
    setPlanId(plan.id);
    const pantry = plan.formData?.pantryItems
      ? plan.formData.pantryItems.split(",").map((s) => s.trim()).filter(Boolean)
      : [];
    setActivePantryItems(pantry);
    setActiveTab("current");
  };

  const handleDeletePlan = (idToDelete) => {
    setPlanHistory(planHistory.filter((p) => p.id !== idToDelete));
  };

  return (
    <div className="app-shell">
      <div className="bg-orb bg-orb-1" />
      <div className="bg-orb bg-orb-2" />

      {toast && <Toast message={toast} onDone={() => setToast(null)} />}

      <TopBar
        onHowTo={() => setModal("howto")}
        onFeature={() => setModal("feature")}
      />

      {modal === "howto" && (
        <Modal title="How to use PantryPlanner" onClose={() => setModal(null)}>
          <HowToUse />
        </Modal>
      )}

      {modal === "feature" && (
        <Modal title="Feature request" onClose={() => setModal(null)}>
          <FeatureRequest
            onClose={() => setModal(null)}
            onSent={() => showToast("Opening your email…")}
          />
        </Modal>
      )}

      <main className="container">
        <section className="hero">
          <h1>Plan smarter meals in under a minute</h1>
          <p className="hero-subtitle">
            Smart weekly dinners built around what you already have — with a
            ready-to-shop grocery list.
          </p>
        </section>

        <section className="layout-grid">
          <div className="panel glass">
            <div className="panel-header">
              <div>
                <p className="eyebrow">Meal planner</p>
                <h2>Customize your week</h2>
              </div>
              <div className="header-actions">
                <button
                  className="ghost-button"
                  type="button"
                  onClick={handleClearCurrent}
                  title="Clear current plan"
                >
                  New plan
                </button>
                <button
                  className="ghost-button ghost-button-danger"
                  type="button"
                  onClick={handleClearAll}
                  title="Reset form and clear all history"
                >
                  Reset all
                </button>
              </div>
            </div>

            <form className="form-grid" onSubmit={handleSubmit}>
              <div className="field">
                <label htmlFor="householdSize">Household size</label>
                <input
                  id="householdSize"
                  name="householdSize"
                  type="number"
                  min="1"
                  value={form.householdSize}
                  onChange={handleChange}
                  placeholder="2"
                />
              </div>

              <div className="field">
                <label htmlFor="budget">Budget ($)</label>
                <input
                  id="budget"
                  name="budget"
                  type="number"
                  min="1"
                  value={form.budget}
                  onChange={handleChange}
                  placeholder="120"
                />
              </div>

              <div className="field field-full">
                <label htmlFor="dietPreference">Diet preference</label>
                <select
                  id="dietPreference"
                  name="dietPreference"
                  value={form.dietPreference}
                  onChange={handleChange}
                >
                  <optgroup label="Popular">
                    <option>Balanced</option>
                    <option>Family Friendly</option>
                    <option>Budget Friendly</option>
                  </optgroup>
                  <optgroup label="Low Carb">
                    <option>Low Carb</option>
                    <option>Keto</option>
                    <option>Carnivore</option>
                    <option>Animal Based</option>
                  </optgroup>
                  <optgroup label="Whole Foods">
                    <option>Paleo</option>
                    <option>Whole30</option>
                    <option>Mediterranean</option>
                  </optgroup>
                  <optgroup label="Performance">
                    <option>High Protein</option>
                  </optgroup>
                  <optgroup label="Plant Based">
                    <option>Vegetarian</option>
                    <option>Vegan</option>
                  </optgroup>
                  <optgroup label="Seasonal">
                    <option>Light & Fresh</option>
                    <option>Summer Grilling</option>
                  </optgroup>
                  <optgroup label="Allergen Friendly">
                    <option>Gluten Free</option>
                    <option>Dairy Free</option>
                  </optgroup>
                </select>
              </div>

              <div className="field field-full">
                <label htmlFor="avoidFoods">Foods to avoid</label>
                <textarea
                  id="avoidFoods"
                  name="avoidFoods"
                  rows="2"
                  value={form.avoidFoods}
                  onChange={handleChange}
                  placeholder="tofu, mushrooms, shellfish"
                />
                <small>Separate items with commas.</small>
              </div>

              <div className="field field-full">
                <label htmlFor="pantryItems">Pantry items to use</label>
                <textarea
                  id="pantryItems"
                  name="pantryItems"
                  rows="3"
                  value={form.pantryItems}
                  onChange={handleChange}
                  placeholder="pasta, onions, chicken broth"
                />
                <small>
                  What you already have or want to use. Salt, pepper, and basic
                  spices are assumed. Separate items with commas.
                </small>
              </div>

              <div className="field field-full">
                <label htmlFor="days">
                  Days to plan{" "}
                  <span className="label-badge">{form.days}</span>
                </label>
                <input
                  id="days"
                  name="days"
                  type="range"
                  min="1"
                  max="7"
                  value={form.days}
                  onChange={handleChange}
                />
                <div className="range-labels">
                  <span>1 day</span>
                  <span>7 days</span>
                </div>
              </div>

              <div className="field field-full">
                <label>Meals per day</label>
                <div className="slot-toggles">
                  {SLOT_ORDER.map((slot) => {
                    const active = form.mealSlots.includes(slot);
                    return (
                      <button
                        key={slot}
                        type="button"
                        className={`slot-toggle ${active ? "active" : ""}`}
                        onClick={() => {
                          const next = active
                            ? form.mealSlots.filter((s) => s !== slot)
                            : [...form.mealSlots, slot].sort(
                                (a, b) => SLOT_ORDER.indexOf(a) - SLOT_ORDER.indexOf(b),
                              );
                          if (next.length > 0) setForm((p) => ({ ...p, mealSlots: next }));
                        }}
                      >
                        {slot === "Breakfast" ? "🌅" : slot === "Lunch" ? "☀️" : "🌙"} {slot}
                      </button>
                    );
                  })}
                </div>
                <small>
                  {form.mealSlots.length * form.days} meals total · ~${(form.budget / (form.mealSlots.length * form.days)).toFixed(0)} per meal
                </small>
              </div>

              {(parsedAvoidFoods.length > 0 || parsedPantryItems.length > 0) && (
                <div className="chip-row">
                  {parsedAvoidFoods.length > 0 && (
                    <div className="chips-block">
                      <span className="chips-label">Avoiding</span>
                      <div className="chips">
                        {parsedAvoidFoods.map((item, idx) => (
                          <span className="chip chip-danger" key={`avoid-${idx}`}>
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {parsedPantryItems.length > 0 && (
                    <div className="chips-block">
                      <span className="chips-label">Pantry</span>
                      <div className="chips">
                        {parsedPantryItems.slice(0, 8).map((item, idx) => (
                          <span className="chip" key={`pantry-${idx}`}>
                            {item}
                          </span>
                        ))}
                        {parsedPantryItems.length > 8 && (
                          <span className="chip">
                            +{parsedPantryItems.length - 8} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <button
                className="primary-button submit-button"
                type="submit"
                disabled={loading}
              >
                {loading ? "Generating…" : "Generate meal plan"}
              </button>

              {error && (
                <div className="alert alert-error" role="alert">
                  <strong>Something went wrong.</strong>
                  <span>{error}</span>
                </div>
              )}
            </form>
          </div>

          <div className="panel glass preview-panel">
            <div className="panel-header">
              <div>
                <p className="eyebrow">What you get</p>
                <h2>Your weekly plan</h2>
              </div>
            </div>

            <div className="tabs" role="tablist">
              <button
                className={`tab-button ${activeTab === "current" ? "active" : ""}`}
                role="tab"
                aria-selected={activeTab === "current"}
                onClick={() => setActiveTab("current")}
              >
                Current Plan
              </button>
              <button
                className={`tab-button ${activeTab === "history" ? "active" : ""}`}
                role="tab"
                aria-selected={activeTab === "history"}
                onClick={() => setActiveTab("history")}
              >
                History ({planHistory.length})
              </button>
            </div>

            <ErrorBoundary>
            {activeTab === "history" ? (
              <div className="plan-history">
                {planHistory.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">📚</div>
                    <h3>No previous plans</h3>
                    <p>Generate your first meal plan to start building history.</p>
                  </div>
                ) : (
                  <div className="history-list">
                    {planHistory.map((plan) => (
                      <div key={plan.id} className="history-item">
                        <div className="history-item-content">
                          <div className="history-item-header">
                            <span className="history-date">
                              {new Date(plan.createdAt).toLocaleDateString(
                                undefined,
                                { month: "short", day: "numeric", year: "numeric" },
                              )}
                            </span>
                            <span className="history-meals">
                              {plan.mealPlan.meals?.length || 0} meals
                            </span>
                          </div>
                          <div className="history-item-details">
                            <span>${plan.formData.budget} budget</span>
                            <span>{plan.formData.householdSize} people</span>
                            <span>{plan.formData.dietPreference}</span>
                          </div>
                        </div>
                        <div className="history-item-actions">
                          <button
                            className="primary-button small"
                            type="button"
                            onClick={() => handleViewPlan(plan)}
                          >
                            View
                          </button>
                          <button
                            className="ghost-button small ghost-button-danger"
                            type="button"
                            onClick={() => handleDeletePlan(plan.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <>
                {!result && !loading && (
                  <div className="empty-state">
                    <div className="empty-icon">🍽️</div>
                    <h3>No plan yet</h3>
                    <p>
                      Fill in your preferences and hit Generate to get a
                      personalized weekly dinner plan with a grocery list.
                    </p>
                  </div>
                )}

                {loading && (
                  <div className="loading-state">
                    <div className="spinner" />
                    <h3>Building your week</h3>
                    <p className="loading-tip">{LOADING_TIPS[loadingTip]}</p>
                  </div>
                )}

                {result && (
                  <div className="results">
                    <div className="summary-grid">
                      <div className="summary-card">
                        <span className="summary-label">Meals planned</span>
                        <span className="summary-value">
                          {(Array.isArray(result.meals) ? result.meals : []).length} across{" "}
                          {new Set((Array.isArray(result.meals) ? result.meals : []).map((m) => m.day)).size} days
                        </span>
                      </div>
                      <div className="summary-card">
                        <span className="summary-label">Est. total</span>
                        <span className="summary-value">
                          ${result.estimatedTotalCost ?? "—"}
                        </span>
                      </div>
                      <div className="summary-card">
                        <span className="summary-label">Grocery items</span>
                        <span className="summary-value">
                          {result.groceryList?.length ?? 0}
                        </span>
                      </div>
                    </div>

                    <div className="section-header">
                      <h3>Meal plan</h3>
                      <span className="section-hint">Tap to expand</span>
                    </div>

                    <div className="meal-list">
                      {(() => {
                        const mealsArr = Array.isArray(result.meals) ? result.meals : [];
                        const byDay = mealsArr.reduce((acc, meal) => {
                          const d = meal.day ?? 1;
                          if (!acc[d]) acc[d] = [];
                          acc[d].push(meal);
                          return acc;
                        }, {});
                        return Object.entries(byDay)
                          .sort(([a], [b]) => Number(a) - Number(b))
                          .map(([day, meals]) => (
                            <div key={day} className="day-group">
                              <div className="day-header">Day {day}</div>
                              {meals
                                .sort((a, b) => SLOT_ORDER.indexOf(a.mealType) - SLOT_ORDER.indexOf(b.mealType))
                                .map((meal, index) => (
                                  <MealCard key={`${meal.name}-${index}`} meal={meal} index={index} />
                                ))}
                            </div>
                          ));
                      })()}
                    </div>

                    <div className="results-lower-grid">
                      <GroceryList
                        items={result.groceryList ?? []}
                        pantryItems={activePantryItems}
                        pantryUsed={result.pantryReport?.used ?? []}
                        onCopy={handleCopyGroceryList}
                        onShare={handleShare}
                      />

                      <section className="subpanel">
                        <div className="section-header">
                          <h3>Prep tips</h3>
                        </div>
                        <ul className="clean-list">
                          {result.mealPrepTips?.map((tip, idx) => (
                            <li key={idx}>{tip}</li>
                          ))}
                        </ul>
                      </section>
                    </div>

                    {activePantryItems.length > 0 && (
                      <PantryUsage
                        report={result.pantryReport}
                        pantryItems={activePantryItems}
                      />
                    )}
                  </div>
                )}
              </>
            )}
            </ErrorBoundary>
          </div>
        </section>
      </main>
    </div>
  );
}

export default App;
